import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Tooltip,
} from "recharts";
import { ReactComponent as ArrowUpRight } from "../../../../../assects/svg/client_details/ArrowUpRight.svg";
import WeeklyMacroDetailPopup, { DayLog } from "./WeeklyMacroDetailPopup";

export type MacroTab = "Calories" | "Adj Protein" | "Carbs" | "Fat" | "GL";

export interface TimeSplitDay {
  day: string;
  before6pm: number;
  after6pm: number;
}

export interface IntakeVsGoalDay {
  day: string;
  actual: number;
  goal: number;
}

export const MACRO_TABS: MacroTab[] = ["Calories", "Adj Protein", "Carbs", "Fat", "GL"];

const TABS = MACRO_TABS;

// ── Palette ───────────────────────────────────────────────────────────────
const ORANGE = "#FF9130";
const YELLOW = "#FFE448";
const RED = "#FF2D55";
const GRAY_BAR = "#D1D5DB";
const GRAY_TRACK = "#EEEEEE";

// ── Per-macro configuration ───────────────────────────────────────────────
interface MacroConfig {
  intakeTitle: string;
  timeSplitTitle: string;
  goalMetColor: string;
  unit: string;
}

export const MACRO_CONFIG: Record<MacroTab, MacroConfig> = {
  "Calories":    { intakeTitle: "Calorie Intake vs Goal",     timeSplitTitle: "Calorie Time Split",     goalMetColor: "#B44AE6", unit: "" },
  "Adj Protein": { intakeTitle: "Adj Protein Intake vs Goal", timeSplitTitle: "Adj. Protein Time Split", goalMetColor: "#6BBDFA", unit: "g" },
  "Carbs":       { intakeTitle: "Carb Intake vs Goal",        timeSplitTitle: "Carb Time Split",         goalMetColor: "#FF9500", unit: "g" },
  "Fat":         { intakeTitle: "Fat Intake vs Goal",         timeSplitTitle: "Fat Time Split",          goalMetColor: "#F25BEC", unit: "g" },
  "GL":          { intakeTitle: "GL Intake vs Goal",          timeSplitTitle: "GL Time Split",           goalMetColor: "#A0CA07", unit: "" },
};

interface MacroDataset {
  timeSplit: TimeSplitDay[];
  intakeVsGoal: IntakeVsGoalDay[];
}

const EMPTY_DATASET: MacroDataset = { timeSplit: [], intakeVsGoal: [] };

const DAY_LABELS_MO_FIRST = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const EMPTY_TIME_SPLIT: TimeSplitDay[] = DAY_LABELS_MO_FIRST.map((day) => ({
  day,
  before6pm: 0,
  after6pm: 0,
}));

const EMPTY_INTAKE_VS_GOAL: IntakeVsGoalDay[] = DAY_LABELS_MO_FIRST.map((day) => ({
  day,
  actual: 0,
  goal: 0,
}));

interface WeeklyMacroIntakeDistributionProps {
  dataByMacro?: Partial<Record<MacroTab, MacroDataset>>;
  days?: DayLog[];
}

const formatK = (v: number) => {
  if (v >= 1000) {
    const val = v / 1000;
    return `${val.toFixed(val % 1 === 0 ? 0 : 1)}k`;
  }
  return `${v}`;
};

const TimeSplitTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const before = payload.find((p: any) => p.dataKey === "before6pm")?.value;
  const after = payload.find((p: any) => p.dataKey === "after6pm")?.value;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">
        {String(label).slice(0, 2)}
      </p>
      {before != null && (
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: ORANGE }} />
          <span style={{ color: ORANGE }} className="font-medium">
            Before 6pm · {before}
          </span>
        </div>
      )}
      {after != null && (
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: YELLOW }} />
          <span style={{ color: "#B8880B" }} className="font-medium">
            After 6pm · {after}
          </span>
        </div>
      )}
    </div>
  );
};

const IntakeVsGoalTooltip =
  (goalMetColor: string) =>
  ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const color = d.metGoal ? goalMetColor : GRAY_BAR;
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
        <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">
          {String(label).slice(0, 2)}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: color }} />
          <span style={{ color }} className="font-medium">
            Intake · {d.actual}
          </span>
        </div>
        {d.over > 0 && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: RED }} />
            <span style={{ color: RED }} className="font-medium">
              Over · +{d.over}
            </span>
          </div>
        )}
        <div className="text-[10px] text-[#8D8D8D] mt-1">Goal · {d.goal}</div>
      </div>
    );
  };

// ── Time Split chart ──────────────────────────────────────────────────────
export const CalorieTimeSplit: React.FC<{
  data: TimeSplitDay[];
  title?: string;
}> = ({ data, title = "Calorie Time Split" }) => {
  // Feed a 7-day zero placeholder when empty so axes always render.
  const displayData = data.length > 0 ? data : EMPTY_TIME_SPLIT;
  const maxVal = data.length
    ? Math.max(...data.flatMap((d) => [d.before6pm, d.after6pm]))
    : 0;
  // Ensure a non-zero domain so the axis stays fixed even when all values are 0.
  const yMax = maxVal > 1000
    ? 2000
    : maxVal > 0
      ? Math.ceil(maxVal / 100) * 100
      : 400;
  const step = yMax / 4;
  const ticks = Array.from({ length: 5 }, (_, i) => i * step);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter mb-3">
        {title}
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={displayData}
            barCategoryGap="20%"
            barGap={2}
            margin={{ top: 20, right: 4, left: 0, bottom: 0 }}
          >
            <Tooltip content={<TimeSplitTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              dy={6}
            />
            <YAxis
              width={35}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              tickFormatter={formatK}
              domain={[0, yMax]}
              ticks={ticks}
              allowDataOverflow
            />
            <Bar
              dataKey="before6pm"
              fill={ORANGE}
              radius={[3, 3, 0, 0]}
              maxBarSize={32}
              minPointSize={2}
              isAnimationActive
            />
            <Bar
              dataKey="after6pm"
              fill={YELLOW}
              radius={[3, 3, 0, 0]}
              maxBarSize={32}
              minPointSize={2}
              isAnimationActive
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-center gap-4 mt-1">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: ORANGE }} />
          <span className="text-[12px] 3xl:text-[14px] text-[#1F1F1F] font-inter">Before 6pm</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: YELLOW }} />
          <span className="text-[12px] 3xl:text-[14px] text-[#1F1F1F] font-inter">After 6pm</span>
        </div>
      </div>
    </div>
  );
};

// ── Intake vs Goal chart ──────────────────────────────────────────────────
export const CalorieIntakeVsGoal: React.FC<{
  data: IntakeVsGoalDay[];
  title?: string;
  goalMetColor?: string;
  unit?: string;
}> = ({ data, title = "Calorie Intake vs Goal", goalMetColor = "#B44AE6", unit = "" }) => {
  // Feed a 7-day zero placeholder when empty so axes always render.
  const sourceData = data.length > 0 ? data : EMPTY_INTAKE_VS_GOAL;
  const chartData = sourceData.map((d) => {
    const metGoal = d.actual >= d.goal;
    const filled = Math.min(d.actual, d.goal);
    const over = Math.max(0, d.actual - d.goal);
    const track = metGoal ? 0 : Math.max(0, d.goal - d.actual);
    return { day: d.day, filled, over, track, actual: d.actual, goal: d.goal, metGoal };
  });

  // Dynamic y-axis — step chosen from a fixed integer palette so labels are
  // always clean (no floating-point noise) and about 4 ticks appear regardless
  // of scale (fat ~30g, protein ~100g, calories ~2000kcal).
  const NICE_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
  const dataMax = data.length
    ? Math.max(...data.map((d) => Math.max(d.actual, d.goal)))
    : 0;
  const yMaxBase = dataMax > 0 ? dataMax : 100;
  const roughStep = yMaxBase / 4;
  const niceStep =
    NICE_STEPS.find((s) => s >= roughStep) ?? NICE_STEPS[NICE_STEPS.length - 1];
  const niceMax = niceStep * Math.ceil(yMaxBase / niceStep);
  const tickCount = Math.round(niceMax / niceStep) + 1;
  const ticks = Array.from({ length: tickCount }, (_, i) => i * niceStep);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          {title}
        </span>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={chartData}
            barCategoryGap="25%"
            margin={{ top: 20, right: 4, left: 0, bottom: 0 }}
          >
            <Tooltip content={IntakeVsGoalTooltip(goalMetColor)} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              dy={6}
            />
            <YAxis
              width={40}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              domain={[0, niceMax]}
              ticks={ticks}
              allowDataOverflow
            />

            <Bar
              dataKey="filled"
              stackId="cal"
              radius={[0, 0, 0, 0]}
              maxBarSize={44}
              minPointSize={2}
              isAnimationActive
            >
              {chartData.map((d, i) => (
                <Cell key={`f-${i}`} fill={d.metGoal ? goalMetColor : GRAY_BAR} />
              ))}
            </Bar>

            <Bar
              dataKey="over"
              stackId="cal"
              fill={RED}
              radius={[3, 3, 0, 0]}
              maxBarSize={44}
              isAnimationActive
              label={(props: any) => {
                const { x, y, width, index } = props;
                const d = chartData[index];
                if (!d || d.over <= 0) return null;
                return (
                  <text
                    x={x + width / 2}
                    y={y - 6}
                    textAnchor="middle"
                    fill={RED}
                    fontSize={11}
                    fontFamily="Inter, sans-serif"
                    fontWeight={600}
                  >
                    {`+${d.over}`}
                  </text>
                );
              }}
            />

            <Bar
              dataKey="track"
              stackId="cal"
              fill={GRAY_TRACK}
              radius={[3, 3, 0, 0]}
              maxBarSize={44}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-center gap-4 mt-1 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: goalMetColor }} />
          <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">Goal met</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: RED }} />
          <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">Over goal</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: GRAY_BAR }} />
          <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">Below goal</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: GRAY_TRACK }} />
          <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">Goal</span>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────
const WeeklyMacroIntakeDistribution: React.FC<WeeklyMacroIntakeDistributionProps> = ({
  dataByMacro,
  days,
}) => {
  const [activeTab, setActiveTab] = useState<MacroTab>("Calories");
  const [popupOpen, setPopupOpen] = useState(false);

  const cfg = MACRO_CONFIG[activeTab];
  const dataset = dataByMacro?.[activeTab] ?? EMPTY_DATASET;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Weekly Macro Intake &amp; Distribution Sources
        </span>
        <button
          type="button"
          onClick={() => setPopupOpen(true)}
          className="text-[#0DB5B5] hover:text-[#20BCBB] transition"
          aria-label="expand"
        >
          <ArrowUpRight className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {TABS.map((tab) => {
          const active = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-full text-[12px] 3xl:text-[14px] font-inter font-medium border transition ${
                active
                  ? "bg-[#1F1F1F] text-white border-[#1F1F1F]"
                  : "bg-white text-[#1F1F1F] border-[#E5E7EB] hover:border-[#8D8D8D]"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <CalorieTimeSplit data={dataset.timeSplit} title={cfg.timeSplitTitle} />
        <CalorieIntakeVsGoal
          data={dataset.intakeVsGoal}
          title={cfg.intakeTitle}
          goalMetColor={cfg.goalMetColor}
          unit={cfg.unit}
        />
      </div>

      <WeeklyMacroDetailPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        timeSplit={dataset.timeSplit}
        intakeVsGoal={dataset.intakeVsGoal}
        days={days}
      />
    </div>
  );
};

export default WeeklyMacroIntakeDistribution;
